/**
 * Permissions a custom role can be granted, grouped by the module they show
 * under in the role drawer. Keys are `<resource>.<action>`. The backend keeps
 * its own copy in src/constants/permissions.ts — keep the two in sync.
 */
export const ROLE_PERMISSION_GROUPS = {
  assets: ["assets.create", "assets.view", "assets.update", "assets.delete"],
  parts: ["parts.create", "parts.view", "parts.update", "parts.delete"],
  products: [
    "products.create",
    "products.view",
    "products.update",
    "products.delete",
  ],
  workOrders: [
    "workOrders.create",
    "workOrders.view",
    "workOrders.update",
    "workOrders.delete",
  ],
  tickets: [
    "tickets.create",
    "tickets.view",
    "tickets.update",
    "tickets.delete",
  ],
  emails: ["emails.create", "emails.view", "emails.update", "emails.delete"],
  procedures: [
    "procedureTemplates.create",
    "procedureTemplates.view",
    "procedureTemplates.update",
    "procedureTemplates.delete",
    "procedureInstances.create",
    "procedureInstances.view",
    "procedureInstances.update",
    "procedureInstances.delete",
  ],
  scheduler: ["scheduler.view", "scheduler.update"],
  insights: [
    "reports.create",
    "reports.view",
    "reports.update",
    "reports.delete",
  ],
  companies: [
    "companies.create",
    "companies.view",
    "companies.update",
    "companies.delete",
  ],
  contacts: [
    "contacts.create",
    "contacts.view",
    "contacts.update",
    "contacts.delete",
  ],
  portals: [
    "portals.create",
    "portals.view",
    "portals.update",
    "portals.delete",
  ],
  quotes: ["quotes.create", "quotes.view", "quotes.update", "quotes.delete"],
  invoices: [
    "invoices.create",
    "invoices.view",
    "invoices.update",
    "invoices.delete",
  ],
  aiAssistants: [
    "aiAssistants.create",
    "aiAssistants.update",
    "aiAssistants.delete",
  ],
} as const satisfies Record<string, readonly string[]>;

export type RolePermissionGroup = keyof typeof ROLE_PERMISSION_GROUPS;
export type RolePermission =
  (typeof ROLE_PERMISSION_GROUPS)[RolePermissionGroup][number];

export const GRANTABLE_PERMISSIONS: RolePermission[] = Object.values(
  ROLE_PERMISSION_GROUPS,
).flat();

/**
 * Settings of a custom role that sit under one of its permissions, keyed by the
 * permission they belong to. Saved with the role's permissions, but shown as a
 * toggle under that permission rather than a checkbox, and only kept while it
 * is ticked. Mirrors the backend's ROLE_PERMISSION_OPTIONS.
 */
export const ROLE_PERMISSION_OPTIONS = {
  /** People in a team may assign work orders to people in other teams too. */
  "workOrders.assignAcrossTeams": "workOrders.update",
} as const satisfies Record<string, RolePermission>;

export type RolePermissionOption = keyof typeof ROLE_PERMISSION_OPTIONS;

/** What a custom role's permissions list can hold. */
export type RoleGrant = RolePermission | RolePermissionOption;

export const isRolePermissionOption = (
  grant: string,
): grant is RolePermissionOption => grant in ROLE_PERMISSION_OPTIONS;

/** The only permissions a VIEW_ONLY role may hold. */
export const VIEW_PERMISSIONS: RolePermission[] = GRANTABLE_PERMISSIONS.filter(
  (permission) => permission.endsWith(".view"),
);

/**
 * Held only by system roles and never grantable — custom roles never get them.
 * Keep in sync with INTERNAL_PERMISSIONS on the backend.
 */
export const INTERNAL_PERMISSIONS = {
  /** Company settings, users, teams and roles. */
  SETTINGS_MANAGE: "settings.manage",
  /** CSV imports. */
  DATA_IMPORT: "data.import",
  /** Bulk delete on list pages. */
  DATA_BULK_DELETE: "data.bulkDelete",
  WORK_ORDERS_ARCHIVE: "workOrders.archive",
  TICKETS_DE_ESCALATE: "tickets.deEscalate",
} as const;

export const SETTINGS_PERMISSION = INTERNAL_PERMISSIONS.SETTINGS_MANAGE;

/**
 * Split off a grantable permission so built-in roles keep their exact access;
 * a custom role gets them along with the one they come from.
 */
export const DERIVED_PERMISSIONS = {
  "workOrders.changeType": "workOrders.update",
  "workOrders.plan": "workOrders.update",
  "assets.updateHistory": "assets.update",
  "parts.deleteStockOperation": "parts.update",
} as const;

/** Any permission key the frontend checks. */
export type Permission =
  | RolePermission
  | keyof typeof DERIVED_PERMISSIONS
  | (typeof INTERNAL_PERMISSIONS)[keyof typeof INTERNAL_PERMISSIONS];

/**
 * Every permission key, by resource then action, so call sites read
 * `can(PERMISSIONS.workOrders.update)` and autocomplete. The values are the
 * plain keys above; the checks below keep this map in step with them.
 */
export const PERMISSIONS = {
  assets: {
    create: "assets.create",
    view: "assets.view",
    update: "assets.update",
    delete: "assets.delete",
    updateHistory: "assets.updateHistory",
  },
  parts: {
    create: "parts.create",
    view: "parts.view",
    update: "parts.update",
    delete: "parts.delete",
    deleteStockOperation: "parts.deleteStockOperation",
  },
  products: {
    create: "products.create",
    view: "products.view",
    update: "products.update",
    delete: "products.delete",
  },
  workOrders: {
    create: "workOrders.create",
    view: "workOrders.view",
    update: "workOrders.update",
    delete: "workOrders.delete",
    changeType: "workOrders.changeType",
    plan: "workOrders.plan",
    archive: "workOrders.archive",
  },
  tickets: {
    create: "tickets.create",
    view: "tickets.view",
    update: "tickets.update",
    delete: "tickets.delete",
    deEscalate: "tickets.deEscalate",
  },
  emails: {
    create: "emails.create",
    view: "emails.view",
    update: "emails.update",
    delete: "emails.delete",
  },
  procedureTemplates: {
    create: "procedureTemplates.create",
    view: "procedureTemplates.view",
    update: "procedureTemplates.update",
    delete: "procedureTemplates.delete",
  },
  procedureInstances: {
    create: "procedureInstances.create",
    view: "procedureInstances.view",
    update: "procedureInstances.update",
    delete: "procedureInstances.delete",
  },
  scheduler: {
    view: "scheduler.view",
    update: "scheduler.update",
  },
  reports: {
    create: "reports.create",
    view: "reports.view",
    update: "reports.update",
    delete: "reports.delete",
  },
  companies: {
    create: "companies.create",
    view: "companies.view",
    update: "companies.update",
    delete: "companies.delete",
  },
  contacts: {
    create: "contacts.create",
    view: "contacts.view",
    update: "contacts.update",
    delete: "contacts.delete",
  },
  portals: {
    create: "portals.create",
    view: "portals.view",
    update: "portals.update",
    delete: "portals.delete",
  },
  quotes: {
    create: "quotes.create",
    view: "quotes.view",
    update: "quotes.update",
    delete: "quotes.delete",
  },
  invoices: {
    create: "invoices.create",
    view: "invoices.view",
    update: "invoices.update",
    delete: "invoices.delete",
  },
  aiAssistants: {
    create: "aiAssistants.create",
    update: "aiAssistants.update",
    delete: "aiAssistants.delete",
  },
  settings: {
    manage: "settings.manage",
  },
  data: {
    import: "data.import",
    bulkDelete: "data.bulkDelete",
  },
} as const satisfies Record<string, Record<string, Permission>>;

type PermissionMap = typeof PERMISSIONS;
type MappedPermission = {
  [R in keyof PermissionMap]: PermissionMap[R][keyof PermissionMap[R]];
}[keyof PermissionMap];

// Each value must be its own `<resource>.<action>` path…
PERMISSIONS satisfies {
  [R in keyof PermissionMap]: {
    [A in keyof PermissionMap[R]]: `${R}.${A & string}`;
  };
};
// …and every Permission must be in the map. A missing key fails here, naming it.
const allPermissionsMapped: Exclude<Permission, MappedPermission> extends never
  ? true
  : Exclude<Permission, MappedPermission> = true;
void allPermissionsMapped;
